import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

import {
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  MenuItem,
  Select,
  IconButton,
  Stack,
  Typography,
  TextField,
  CircularProgress,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Url } from "../url/url";


const statusColors = {
  Pending: "warning",
  Contacted: "info",
  Closed: "success",
};

const PetEnquriy = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const fetchEnquiries = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const res = await axios.get(`${Url}/api/pet-enquiry`);
      setEnquiries(res.data?.data || []);
    } catch (error) {
      console.error("Error fetching pet enquiries:", error);
      setErrorMsg(
        error?.response?.data?.message || "Enquiries load nahi ho payi"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEnquiries();
  }, [fetchEnquiries]);

  const handleStatusChange = async (enquiryId, newStatus) => {
    const prevEnquiries = enquiries;
    // optimistic update
    setEnquiries((prev) =>
      prev.map((e) => (e._id === enquiryId ? { ...e, status: newStatus } : e))
    );
    try {
      await axios.put(`${Url}/api/pet-enquiry/${enquiryId}`, { status: newStatus });
    } catch (error) {
      console.error("Error updating status:", error);
      setEnquiries(prevEnquiries); // rollback on failure
    }
  };

  const handleDelete = async (enquiryId) => {
    if (!window.confirm("Ye enquiry delete karni hai?")) return;
    try {
      await axios.delete(`${Url}/api/pet-enquiry/${enquiryId}`);
      setEnquiries((prev) => prev.filter((e) => e._id !== enquiryId));
    } catch (error) {
      console.error("Error deleting enquiry:", error);
    }
  };

  const filteredEnquiries = enquiries.filter((e) => {
    const q = search.toLowerCase();
    return (
      e.name?.toLowerCase().includes(q) ||
      e.phone?.toLowerCase().includes(q) ||
      e.email?.toLowerCase().includes(q) ||
      e.breedName?.toLowerCase().includes(q)
    );
  });

  return (
    <Card sx={{ p: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Typography variant="h5">Pet Breed Enquiries</Typography>
        <TextField
          size="small"
          placeholder="Search by name, phone, email, breed..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: { xs: "100%", sm: 300 } }}
        />
      </Stack>

      {loading ? (
        <Stack alignItems="center" py={5}>
          <CircularProgress />
        </Stack>
      ) : errorMsg ? (
        <Typography color="error" textAlign="center" py={5}>
          {errorMsg}
        </Typography>
      ) : filteredEnquiries.length === 0 ? (
        <Typography color="text.secondary" textAlign="center" py={5}>
          Koi enquiry nahi mili.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Breed</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Preferred Time</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEnquiries.map((enq) => (
                <TableRow key={enq._id} hover>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Avatar
                        variant="rounded"
                        src={enq.breed?.thumbnailImage}
                        alt={enq.breedName}
                      />
                      <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                        {enq.breedName || enq.breed?.name || "—"}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>{enq.name}</TableCell>
                  <TableCell>{enq.phone}</TableCell>
                  <TableCell>{enq.email}</TableCell>
                  <TableCell>{enq.preferredTime}</TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={enq.status}
                      onChange={(e) => handleStatusChange(enq._id, e.target.value)}
                      renderValue={(val) => (
                        <Chip label={val} color={statusColors[val] || "default"} size="small" />
                      )}
                    >
                      <MenuItem value="Pending">Pending</MenuItem>
                      <MenuItem value="Contacted">Contacted</MenuItem>
                      <MenuItem value="Closed">Closed</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    {enq.createdAt ? new Date(enq.createdAt).toLocaleDateString() : "—"}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => setSelected(enq)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(enq._id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* VIEW ENQUIRY DIALOG */}
      <Dialog open={!!selected} onClose={() => setSelected(null)} fullWidth maxWidth="sm">
        <DialogTitle>Enquiry Details</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Stack spacing={1.5}>
              <Typography><b>Breed:</b> {selected.breedName || selected.breed?.name}</Typography>
              <Typography><b>Name:</b> {selected.name}</Typography>
              <Typography><b>Phone:</b> {selected.phone}</Typography>
              <Typography><b>Email:</b> {selected.email}</Typography>
              <Typography><b>Address:</b> {selected.address}</Typography>
              <Typography><b>Preferred Time:</b> {selected.preferredTime}</Typography>
              <Typography><b>Status:</b> {selected.status}</Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default PetEnquriy;